import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useForm } from 'react-hook-form';
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "../../src/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../src/components/ui/form"
import { Input } from "../../src/components/ui/input"

const emailFormSchema = z.object({
  email: z.string().min(2).max(50),
  password: z.string().min(8).max(12)
})

const tokenFormSchema = z.object({
  token: z.string().min(1, 'Token is required')
})

const Login: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'email' | 'token'>('email');

  const emailForm = useForm<z.infer<typeof emailFormSchema>>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: "",
      password: ""
    },
  })

  const tokenForm = useForm<z.infer<typeof tokenFormSchema>>({
    resolver: zodResolver(tokenFormSchema),
    defaultValues: {
      token: ""
    },
  })

  const [error, setError] = useState('');
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();

  const handleEmailSubmit = async (data: z.infer<typeof emailFormSchema>) => {
    try {
      setError('');
      await login(data.email, data.password);
      navigate('/');
    } catch (err) {
      setError('Failed to log in. Check your email and password.');
      console.error(err);
    }
  };

  const handleTokenSubmit = async (data: z.infer<typeof tokenFormSchema>) => {
    try {
      setError('');
      await loginWithToken(data.token);
      navigate('/download');
    } catch (err) {
      setError('Invalid or expired token. Please try again.');
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in
          </h2>
        </div>

        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 py-2 px-4 text-sm font-medium ${
              activeTab === 'email'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Email
          </button>
          <button
            onClick={() => setActiveTab('token')}
            className={`flex-1 py-2 px-4 text-sm font-medium ${
              activeTab === 'token'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Token
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {activeTab === 'email' && (
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="mt-8 space-y-6">
              <FormField
                control={emailForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="enter your email account" {...field} />
                    </FormControl>
                    <FormDescription>
                      Please enter with your registered email account
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={emailForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="enter your password" {...field} />
                    </FormControl>
                    <FormDescription>
                      Please enter with your registered password
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Sign in with Email
              </Button>
            </form>
          </Form>
        )}

        {activeTab === 'token' && (
          <Form {...tokenForm}>
            <form onSubmit={tokenForm.handleSubmit(handleTokenSubmit)} className="mt-8 space-y-6">
              <FormField
                control={tokenForm.control}
                name="token"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Access Token</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="paste your access token"
                        {...field}
                        className="font-mono text-sm"
                      />
                    </FormControl>
                    <FormDescription>
                      Token expires 24 hours after first use
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                Sign in with Token
              </Button>
            </form>
          </Form>
        )}
      </div>
    </div>
  );
};

export default Login;
